/**
 * TASK-2560 — NovoChamado MR-D
 *
 * Live preview footer mostrando como o chamado vai aparecer pro time.
 * Atualiza em tempo real. So renderiza quando ha title.trim().
 */
import { Box, Group, Text, useMantineTheme } from '@mantine/core'
import type { TipoOption } from './TipoChamadoToggleGroup'

const PRIORITY_COLORS: Record<string, string> = {
  low: 'gray',
  medium: 'blue',
  high: 'orange',
  critical: 'red',
}

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
}

interface Props {
  title: string
  tipoOption: TipoOption | undefined
  priority: string
  tenantName?: string | null
  teamName?: string | null
}

export function NovoChamadoPreview({ title, tipoOption, priority, tenantName, teamName }: Props) {
  const theme = useMantineTheme()
  const t = title.trim()
  if (!t) return null

  const tipoColor = tipoOption ? (theme.colors[tipoOption.color]?.[6] ?? theme.colors.gray[6]) : theme.colors.gray[6]
  const priorityColor = theme.colors[PRIORITY_COLORS[priority] ?? 'gray']?.[6] ?? theme.colors.gray[6]
  const titleTrunc = t.length > 80 ? t.slice(0, 77) + '…' : t

  return (
    <Box
      role="status"
      aria-live="polite"
      p="sm"
      mt="xs"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 8,
        background: 'var(--ai-bg-elevated)',
      }}
    >
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4} style={{ letterSpacing: 0.5 }}>
        Pré-visualização
      </Text>
      <Group gap={6} wrap="wrap" align="center">
        {tipoOption && (
          <Group gap={4} wrap="nowrap">
            <Box style={{ color: tipoColor, display: 'flex' }}>{tipoOption.icon}</Box>
            <Text size="sm" fw={500} c={tipoColor}>{tipoOption.shortLabel}</Text>
          </Group>
        )}
        <Text size="xs" c="dimmed">·</Text>
        <Text size="sm" fw={500} c={priorityColor}>{PRIORITY_LABELS[priority] ?? priority}</Text>
        {tenantName && (
          <>
            <Text size="xs" c="dimmed">·</Text>
            <Text size="sm" c="gray.7">{tenantName}</Text>
          </>
        )}
        {teamName && (
          <>
            <Text size="xs" c="dimmed">→</Text>
            <Text size="sm" c="gray.7" fw={500}>{teamName}</Text>
          </>
        )}
      </Group>
      <Text size="sm" fw={500} mt={4} lineClamp={2}>"{titleTrunc}"</Text>
    </Box>
  )
}
